import { type FC, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { UploadDocumentModal } from "@/shared/components/overlay/UploadDocumentModal";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { useDocuments } from "@/shared/hooks/useDocumentStorage";
import { useUploadPaywall } from "@/shared/hooks/useUploadPaywall";
import { DocumentType } from "@/shared/types/documentTypes";
import { invalidateUploadedDocumentQueries } from "@/shared/utils/cacheInvalidation";

import { KeyDocumentsCard } from "./KeyDocumentsCard";

interface KeyDocumentsUploadCardProps {
  onPaywall: () => void;
  className?: string;
}

export const KeyDocumentsUploadCard: FC<KeyDocumentsUploadCardProps> = ({
  onPaywall,
  className,
}) => {
  const { person } = useAppContext();
  const queryClient = useQueryClient();
  const { data: documentsData } = useDocuments();
  const { shouldShowPaywall, isPremium, remainingSlots } = useUploadPaywall();
  const [keyDocument, setKeyDocument] = useState<{ type: DocumentType; label: string } | null>(
    null,
  );

  const handleAddDocument = (documentType: DocumentType, label: string) => {
    if (shouldShowPaywall) {
      onPaywall();
    } else {
      setKeyDocument({ type: documentType, label });
    }
  };

  const handleUploaded = () => {
    setKeyDocument(null);
    if (person?.estate_id) {
      invalidateUploadedDocumentQueries(queryClient, person.estate_id);
    }
  };

  const handleLimitExceeded = () => {
    setKeyDocument(null);
    onPaywall();
  };

  return (
    <>
      <KeyDocumentsCard
        documents={documentsData?.documents || []}
        onAddDocument={handleAddDocument}
        className={className}
      />
      {person?.estate_id && keyDocument && (
        <UploadDocumentModal
          isOpen
          onClose={() => setKeyDocument(null)}
          estateId={person.estate_id}
          size="lg"
          documentType={keyDocument.type}
          title={`Upload ${keyDocument.label}`}
          maxFiles={isPremium ? 1 : Math.min(1, remainingSlots)}
          onLimitExceeded={handleLimitExceeded}
          onSuccess={handleUploaded}
        />
      )}
    </>
  );
};
